
import { supabase } from "@/integrations/supabase/client";
import { Call, Lead, LeadStatus } from "@/types";
import { updateCallFromConversationStatus, pollConversationStatus } from "./conversationStatusService";

// Interface defining the outbound call result
interface OutboundCallResult {
  success: boolean;
  callId?: string;
  conversationId?: string;
  error?: string;
}

/**
 * Initiates an outbound call to a specified phone number using an ElevenLabs agent
 * 
 * @param phoneNumber - The destination phone number in international format (e.g., +19876543210)
 * @param agentId - The ElevenLabs agent ID to use for the call
 * @param voiceId - Optional voice ID to override the agent's default voice
 * @param scriptOverride - Optional script to override the agent's first message
 * @param phoneNumberId - Optional phone number ID to use for the call
 * @param variables - Optional variables to substitute in the conversation
 * @returns A promise that resolves to the result of the call initiation
 */
export const initiateOutboundCall = async (
  phoneNumber: string,
  agentId: string,
  voiceId?: string,
  scriptOverride?: string,
  phoneNumberId?: string,
  variables?: Record<string, string>
): Promise<{ 
  success: boolean; 
  callId?: string; 
  conversationId?: string;
  error?: string 
}> => {
  try {
    console.log(`Initiating outbound call to ${phoneNumber}`);
    
    // Call the Edge Function to initiate the call
    const { data, error } = await supabase.functions.invoke("elevenlabs-outbound-call", {
      body: { 
        phoneNumber, 
        agentId, 
        voiceId,
        scriptOverride,
        phoneNumberId,
        variables
      },
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || "Failed to initiate call");

    // Extract the call ID and conversation ID from the response
    const callId = data.callId;
    const conversationId = data.conversationId;
    
    console.log(`Call initiated with ID: ${callId}, conversation ID: ${conversationId || 'N/A'}`);
    
    // Start polling for call status if we have a conversation ID
    if (conversationId) {
      // Create a call record in the database
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .insert({
          id: callId, // Use the call ID from ElevenLabs as our call ID
          lead_id: 'dummy-lead-id', // We'll update this later
          status: 'in-progress',
          meta: {
            conversation_id: conversationId,
            agent_id: agentId,
            variables: variables
          }
        })
        .select()
        .single();
      
      if (callError) {
        console.error("Error creating call record:", callError);
      } else {
        console.log("Call record created:", callData);
        
        // Start polling for call status updates in the background
        pollConversationStatus(callId, conversationId)
          .then(result => {
            console.log("Polling complete:", result);
          })
          .catch(pollError => {
            console.error("Error polling conversation status:", pollError);
          });
      }
    }

    return { 
      success: true, 
      callId,
      conversationId
    };
  } catch (error) {
    console.error("Error initiating outbound call:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};

/**
 * Checks the status of a call using the ElevenLabs conversation ID
 */
export const checkCallStatus = async (
  callId: string,
  conversationId: string
): Promise<{ success: boolean; status?: string; error?: string }> => {
  try {
    // Update the call record with the latest conversation status
    const updateResult = await updateCallFromConversationStatus(callId, conversationId);
    
    if (!updateResult.success) {
      throw new Error(updateResult.error || "Failed to update call status");
    }
    
    // Get the updated call record
    const { data: call, error } = await supabase
      .from('calls')
      .select('status')
      .eq('id', callId)
      .single();
    
    if (error) throw error;
    
    return {
      success: true,
      status: call.status
    };
  } catch (error) {
    console.error("Error checking call status:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "An unknown error occurred" 
    };
  }
};
